import React, { useState } from 'react';
import { Form } from 'antd';
import type { FormProps } from 'antd';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { DocumentPayload } from '@nxsys/types/models/document';
import { defaultDocumentTypeOptionsGroup } from '@nxsys/constants/AppConstants';
import styled from 'styled-components';

const FormWrapper = styled.div`
  .ant-form-item .ant-form-item-label {
    display: flex;
    align-items: center;
  }
  .ant-form-item .ant-form-item-label > label {
    min-height: unset;
  }
`;

type CreateModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  onCreatedDocument: (params: DocumentPayload) => void;
  defaultValues?: any;
  listDisabled?: string[];
};

const CreateModal: React.FC<CreateModalProps> = ({
  title,
  isOpen,
  toggle,
  onCreatedDocument,
  defaultValues = {},
  listDisabled = [],
}: CreateModalProps) => {
  const [form] = Form.useForm();
  const [isSubmitPending, setIsSubmitPending] = useState(false);

  const onCancel = () => {
    console.log('Cancel');
    form.resetFields();
    toggle();
  };

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.log('Success:', values);
    setIsSubmitPending(true);
    const documentFile = values?.documentFile;
    const params = {
      ...values,
      agencyRef: values?.agencyRef?.value || null,
      documentType: values?.documentType?.value || null,
      expiryDate: values?.expiryDate?.toDate() || null,
      documentDate: values?.documentDate?.toDate() || null,
      documentName: documentFile?.name || null,
    };
    await onCreatedDocument(params);
    setIsSubmitPending(false);
    onCancel();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: 'Cancel',
          variant: 'outlined',
          buttonType: 'primary',
          onClick: onCancel,
        },
        {
          name: 'Save',
          buttonType: 'primary',
          onClick: () => {
            console.log('Create');
            form.submit();
          },
          loading: isSubmitPending,
          disabled: isSubmitPending,
        },
      ]}
    >
      <FormWrapper>
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          initialValues={defaultValues}
        >
          <Form.Item
            label="Agency"
            name="agencyRef"
            rules={[{ required: true, message: 'Please select agency' }]}
            wrapperCol={{ span: 24 }}
            labelCol={{ span: 24 }}
            // validateStatus="error"
            // help="Should be combination of numbers & alphabets"
          >
            <AppSelectSync
              type="agency"
              apiType="searchAgency"
              placeholder="Select agency"
              onChangeValue={(value: any) => {
                form.setFieldsValue({ agencyRef: value });
              }}
              disabled={listDisabled.includes('agencyRef')}
            />
          </Form.Item>
          <Form.Item
            label="Document Title"
            name="documentTitle"
            rules={[{ required: true, message: 'Please input document title' }]}
            wrapperCol={{ span: 24 }}
            labelCol={{ span: 24 }}
            // validateStatus="error"
            // help="Should be combination of numbers & alphabets"
          >
            <AppInput placeholder="Enter document title" />
          </Form.Item>
          <Form.Item
            label="Document Type"
            name="documentType"
            rules={[{ required: true, message: 'Please input document type' }]}
            wrapperCol={{ span: 24 }}
            labelCol={{ span: 24 }}
            // validateStatus="error"
            // help="Should be combination of numbers & alphabets"
          >
            <AppSelect
              options={defaultDocumentTypeOptionsGroup}
              placeholder="Select document type"
            />
          </Form.Item>
          <Form.Item
            label="Expiry Date"
            name="expiryDate"
            wrapperCol={{ span: 24 }}
            labelCol={{ span: 24 }}
            // validateStatus="error"
            // help="Should be combination of numbers & alphabets"
          >
            <AppDatePicker />
          </Form.Item>
          <Form.Item
            label="Document Date"
            name="documentDate"
            wrapperCol={{ span: 24 }}
            labelCol={{ span: 24 }}
            // validateStatus="error"
            // help="Should be combination of numbers & alphabets"
          >
            <AppDatePicker />
          </Form.Item>
          <Form.Item
            label="Attachment"
            name="documentFile"
            rules={[
              { required: true, message: 'Please select your attchment' },
            ]}
            wrapperCol={{ span: 24 }}
            labelCol={{ span: 24 }}
            // validateStatus="error"
            // help="Should be combination of numbers & alphabets"
          >
            <AppUploadFile
              onChange={(file: File) => {
                form.setFieldsValue({ documentFile: file });
              }}
            />
          </Form.Item>
        </AppFormContainer>
      </FormWrapper>
    </AppModalContainer>
  );
};

export default CreateModal;
